import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { withTheme } from "styled-components";
import {
  decimalPlaces,
  roundMealValue,
  UNKNOWN_NUTRIENT_VALUE,
} from "../../../helpers/mealsHelper";
import { dailyTargetIds } from "../../../modules/constants";
import DefaultTable, { Line } from "../../ui-library/CustomTable/DefaultTable";

const NutritionTable = (props) => {
  const {
    theme,
    nutritionFields,
    selectedItems,
    nutritionalInfo,
    setNutritionalInfo,
    fetchNutritionFields,
    tableHeaders,
  } = props;

  const { t } = useTranslation();

  const [totalitems, setTotalitems] = useState(0);

  const lineFields = useMemo(() => ["translationId", "value"], []);

  const setupNutritionFields = useCallback(() => {
    fetchNutritionFields();
  }, [fetchNutritionFields]);

  useEffect(() => {
    setupNutritionFields();
  }, [setupNutritionFields]);

  const getNutritionValue = useCallback(
    (searchAttrId, unit) => {
      if (!selectedItems || selectedItems?.length === 0) {
        return {
          displayValue: UNKNOWN_NUTRIENT_VALUE,
          numericValue: 0,
        };
      }

      const attrNutrients = selectedItems.map((item) => {
        const origServingWeight = item?.defaultMeasure?.serving_weight ?? 1;

        const currentServingWeight = item?.selectedMeasure?.serving_weight ?? 1;

        const measureQty = item?.selectedMeasure?.qty ?? 1;

        const qty = item?.qty ?? 0;
        const nutrientItem = item.fullNutrients.find(
          ({ attr_id }) => attr_id === searchAttrId
        );

        let nutrientValue = null;

        if (
          nutrientItem &&
          origServingWeight > 0 &&
          currentServingWeight > 0 &&
          qty > 0 &&
          measureQty > 0
        ) {
          const weightPerGram = nutrientItem.value / origServingWeight;
          nutrientValue =
            (weightPerGram * currentServingWeight * qty) / measureQty;
        }

        //divide fullnutrient value by serving_weight_grams
        //this gets the nutrient for 1g of food
        //then get the grams of the selected dropboxitem
        //multiply the nutrient for 1g of food by the grams of the selected unit
        //multiply by the quantity of selected item

        return nutrientValue;
      });

      let totalNutrient = attrNutrients.reduce((a, b) => {
        if (a === null && b === null) {
          return a;
        }
        return a + b;
      }, null);

      const normalizedTotal =
        totalNutrient !== null && totalNutrient >= 0
          ? roundMealValue(totalNutrient)
          : null;

      if (searchAttrId === dailyTargetIds.CALORIES_ATTR_ID) {
        return {
          displayValue: `${
            normalizedTotal !== null
              ? normalizedTotal.toFixed(decimalPlaces)
              : Number("0").toFixed(decimalPlaces)
          }${unit}`,
          numericValue: totalNutrient || 0,
        };
      }

      return {
        displayValue:
          normalizedTotal !== null
            ? `${normalizedTotal.toFixed(decimalPlaces)}${unit}`
            : UNKNOWN_NUTRIENT_VALUE,
        numericValue: totalNutrient,
      };
    },
    [selectedItems]
  );

  useEffect(() => {
    const tmpNutritionalInfo = nutritionFields.map((nutritionField) => {
      const tmpValue = getNutritionValue(
        nutritionField.attr_id,
        nutritionField.unit
      );

      return {
        attr_id: nutritionField.attr_id,
        title: nutritionField.title,
        translationId: nutritionField.translationId,
        chartTitle: nutritionField.chartTitle
          ? t(nutritionField.chartTitle)
          : t(nutritionField.translationId),
        aboutMealLetter: nutritionField.aboutMealLetter,
        unit: nutritionField.unit,
        value: tmpValue.displayValue,
        numericValue: tmpValue.numericValue,
        isVisibleInTable: nutritionField.isVisibleInTable,
      };
    });

    setNutritionalInfo(tmpNutritionalInfo);
    setTotalitems(
      tmpNutritionalInfo.filter((item) => item.isVisibleInTable).length
    );
  }, [
    selectedItems,
    nutritionFields,
    getNutritionValue,
    setNutritionalInfo,
    t,
  ]);

  const defaultProps = {
    headers: tableHeaders,
    maincolor: theme?.darkPurple,
    totalitems: totalitems,
    hasSearch: false,
    haspagesizefilter: false,
    haspagination: false,
    searchedParams: {
      search: "",
    },
    topControlsClass: "hide",
    responsivetable: 0,
  };

  return (
    <div data-testid="nutrition-table">
      <DefaultTable {...{ ...defaultProps }} lineHeaderEnabled={false}>
        {nutritionalInfo
          .filter((item) => item.isVisibleInTable)
          .map((selectedItem, index) => {
            return (
              <Line key={`table_line_${index}`}>
                {lineFields.map((field, fieldIndex) => {
                  return (
                    <span key={`table_line_${index}_col_${fieldIndex}`}>
                      {field === "translationId"
                        ? t(selectedItem[field])
                        : selectedItem[field]}
                    </span>
                  );
                })}
              </Line>
            );
          })}
      </DefaultTable>
    </div>
  );
};

NutritionTable.propTypes = {
  nutritionFields: PropTypes.array.isRequired,
  selectedItems: PropTypes.array.isRequired,
  nutritionalInfo: PropTypes.array.isRequired,
  setNutritionalInfo: PropTypes.func.isRequired,
  tableHeaders: PropTypes.array.isRequired,
};

export default withTheme(NutritionTable);
